/**
 * navigation/productHandoff.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Centralized cross-product auth — the platform side of the handoff.
 *
 * A product's "Sign up / Try it" CTA on the Softknack website sends the user
 * here with three query params:
 *   ?product=<slug>&return_to=<product app URL>&state=<nonce>
 * and writes the same `state` to a short-lived `sk_auth_state` cookie scoped to
 * the parent domain (.softknack.com), readable by both the website and the
 * platform.
 *
 * This module:
 *   1. reads + state-verifies that handoff (CSRF / first-party-initiation check),
 *   2. persists the safe parts so they survive the signup → verify-email → login
 *      round-trip (which navigates away from the SPA and back),
 *   3. performs the ONE redirect back into the product after auth — with the
 *      target validated against the server allow-list (config/products).
 *
 * Web-only: the whole cross-product redirect is a browser concern. On native
 * every export is a safe no-op. The session itself rides the existing
 * httpOnly `.softknack.com` cookie — no token is ever placed in a URL here.
 */
import { Platform } from 'react-native';
import { prefetchRedirectTargets, resolveReturnTarget } from '../config/products';
import { isNotInPlan, notInPlanUrl, orgIdFrom } from './planGate';

const STORAGE_KEY = 'sk_auth_handoff';
const STATE_COOKIE = 'sk_auth_state';
const PARAM_KEYS = ['product', 'return_to', 'state'] as const;

export interface ProductHandoff {
  product: string;
  returnTo: string | null;
  state: string;
}

/** The persisted, already-state-verified subset (no nonce — it's single-use). */
export interface PersistedHandoff {
  product: string;
  returnTo: string | null;
}

const isWeb = (): boolean => Platform.OS === 'web' && typeof window !== 'undefined';

/* ── URL parsing ──────────────────────────────────────────────────────────── */

/** Read product/return_to/state from the current URL (web only). */
function readHandoffFromUrl(): ProductHandoff | null {
  if (!isWeb()) return null;
  const q = new URLSearchParams(window.location.search);
  const product = q.get('product');
  const state = q.get('state');
  // Both product and state are required; return_to is optional (defaults to the
  // product root server-side via the allow-list).
  if (!product || !state) return null;
  return { product, returnTo: q.get('return_to'), state };
}

/** Remove only our handoff params from the URL, preserving any others. */
function stripHandoffParamsFromUrl(): void {
  if (!isWeb()) return;
  try {
    const url = new URL(window.location.href);
    PARAM_KEYS.forEach((k) => url.searchParams.delete(k));
    const query = url.searchParams.toString();
    window.history.replaceState({}, '', `${url.pathname}${query ? `?${query}` : ''}`);
  } catch {
    /* history API unavailable — non-fatal */
  }
}

/* ── State (CSRF) verification ────────────────────────────────────────────── */

function readCookie(name: string): string | null {
  if (!isWeb()) return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/** Expire the state cookie on both the host and the registrable parent domain. */
function clearStateCookie(): void {
  if (!isWeb()) return;
  const base = `${STATE_COOKIE}=; Max-Age=0; path=/`;
  document.cookie = base;
  const parts = window.location.hostname.split('.');
  if (parts.length >= 2) {
    document.cookie = `${base}; domain=.${parts.slice(-2).join('.')}`;
  }
}

/**
 * Compare the URL `state` against the website-set cookie, then clear the cookie
 * (single-use). A match proves the auth visit was initiated by a genuine
 * first-party CTA — a forged link that drops a victim here with an
 * attacker-chosen return_to won't carry the matching cookie.
 */
function verifyState(handoff: ProductHandoff): boolean {
  if (!isWeb()) return false;
  const cookieState = readCookie(STATE_COOKIE);
  clearStateCookie();
  return Boolean(cookieState) && cookieState === handoff.state;
}

/* ── Persistence (survives the signup→verify→login round-trip) ─────────────── */

function persistHandoff(h: ProductHandoff): void {
  if (!isWeb()) return;
  try {
    const safe: PersistedHandoff = { product: h.product, returnTo: h.returnTo };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
  } catch {
    /* storage unavailable — the in-URL handoff still covers the happy path */
  }
}

/**
 * A plan paid for on the website (pay before signup) names its product, but the
 * buyer arrives from Cashfree with no handoff params or state cookie. Signup
 * calls this with the product of the server-read order, so that once the email
 * is verified and the buyer signs in, redirectToProduct() opens the product they
 * paid for (salon.softknack.com) instead of the Platform shell. A handoff the
 * buyer already carries is kept; return_to stays empty, so the target is the
 * product's own root from the server allow-list.
 */
export function persistPaidProductHandoff(product: string): void {
  if (!isWeb() || !/^[a-z][a-z_]{1,31}$/.test(product)) return;
  if (restoreHandoff()) return;
  persistHandoff({ product, returnTo: null, state: '' });
}

/**
 * The verification email of a product that signs in through the Platform names
 * that product (`?mode=verify&token=…&product=salon`). Recording it here means
 * the sign-in that follows opens the product, even when the link is opened in
 * a different browser from the one that signed up (no handoff stored there).
 * Only the slug is taken; where it leads still comes from the server allow-list.
 */
export function captureProductFromVerifyLink(): void {
  if (!isWeb()) return;
  const q = new URLSearchParams(window.location.search);
  const product = q.get('product');
  if (q.get('mode') === 'verify' && product) persistPaidProductHandoff(product);
}

/** The persisted, state-verified handoff, if any. Used by the signup screen to
 *  scope signup to the originating product, and by redirectToProduct(). */
export function restoreHandoff(): PersistedHandoff | null {
  if (!isWeb()) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.product === 'string'
      ? { product: parsed.product, returnTo: parsed.returnTo ?? null }
      : null;
  } catch {
    return null;
  }
}

function clearPersistedHandoff(): void {
  if (!isWeb()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* non-fatal */
  }
}

/* ── Logging ──────────────────────────────────────────────────────────────── */

/**
 * One readable line per handoff step. The details go in as a JSON string, not
 * as a second object argument: anything that joins console arguments into text
 * (a log shipper, a headless-browser console capture) prints an object as
 * `[object Object]`, which hid the product and target this line exists to show.
 *
 * Never throws. A broken or patched console must not be able to abort the
 * redirect it is describing — that would leave the handoff unconsumed and the
 * user in the wrong app.
 */
function logHandoff(step: 'capture' | 'redirect' | 'not-in-plan', detail: string | Record<string, unknown>): void {
  try {
    const text = typeof detail === 'string' ? detail : JSON.stringify(detail);
    // eslint-disable-next-line no-console
    console.info(`[auth-handoff] ${step}: ${text}`);
  } catch {
    /* logging is best-effort */
  }
}

/* ── Public API used by the navigator ──────────────────────────────────────── */

/**
 * Capture a handoff present in the URL at boot: verify its state nonce and, if
 * valid, persist it and consume the URL params (so a reload can't re-trigger or
 * re-verify against an already-cleared cookie). Call once on app boot. A forged
 * or stale handoff is dropped silently — the user just sees a normal platform
 * login and ends up in the platform shell.
 */
export function captureHandoffFromUrl(): void {
  const fromUrl = readHandoffFromUrl();
  if (!fromUrl) {
    logHandoff('capture', 'no product/return_to/state in URL (no product CTA).');
    return;
  }
  const ok = verifyState(fromUrl);
  logHandoff('capture', { product: fromUrl.product, returnTo: fromUrl.returnTo, stateMatched: ok });
  if (ok) {
    persistHandoff(fromUrl);
    // Read the redirect allow-list while the user types (see prefetchRedirectTargets).
    prefetchRedirectTargets();
  }
  // Either way, consume the params — verified handoffs are now persisted, and
  // unverified ones must not linger in the URL.
  stripHandoffParamsFromUrl();
}

/** Set once the browser has been sent into a product; a page unload resets it. */
let _redirectStarted = false;

/**
 * If a verified handoff is pending, resolve its safe redirect target against the
 * server allow-list and navigate the browser into the product. Consumes the
 * handoff (single-use) regardless of outcome.
 *
 * Returns true when a redirect is under way (caller should stop rendering the
 * platform shell), false when there's nothing to do / the target was rejected.
 *
 * Two callers can race here — the boot probe and the sign-in screen's
 * enterApp(), or a double-pressed Sign in. The handoff is therefore taken out of
 * storage BEFORE the first await, so only one caller ever sees it, and a caller
 * that arrives after the browser has been sent away reuses that answer instead
 * of navigating a second time.
 *
 * `loadMe` (GET /v1/me) lets it refuse a product the org did not buy: the user is
 * sent to the platform's not-in-plan page instead (see planGate). It is passed
 * in rather than imported because services/auth imports this module. Without
 * it, or if it fails, the redirect goes ahead exactly as before.
 */
export async function redirectToProduct(loadMe?: () => Promise<unknown>): Promise<boolean> {
  if (!isWeb()) return false;
  if (_redirectStarted) return true; // already leaving — never redirect twice
  const persisted = restoreHandoff();
  clearPersistedHandoff(); // single-use: consume before awaiting, so a racing caller finds nothing
  if (!persisted) {
    logHandoff('redirect', 'no persisted handoff → staying in platform shell.');
    return false;
  }

  /*
   * The target and the plan check go TOGETHER. They were one after the other —
   * the allow-list read, then /me — two round trips between "Sign in" and the
   * product opening. A failed /me fails open (null): a /me hiccup must never
   * strand a user who did buy the product.
   */
  const [target, me] = await Promise.all([
    resolveReturnTarget(persisted.product, persisted.returnTo),
    loadMe ? loadMe().catch(() => null) : Promise.resolve(null),
  ]);
  logHandoff('redirect', { product: persisted.product, returnTo: persisted.returnTo, resolvedTarget: target });

  if (!target) return false;

  let destination = target;
  if (me && isNotInPlan(me, persisted.product)) {
    destination = notInPlanUrl(persisted.product);
    logHandoff('not-in-plan', { product: persisted.product, orgId: orgIdFrom(me) });
  }

  _redirectStarted = true;
  window.location.assign(destination);
  return true;
}

/** Test-only: forget that a redirect was started (a real page unloads instead). */
export function __resetRedirectLatchForTests(): void {
  _redirectStarted = false;
}
